import { formatParties, toTitleCasePt } from "@/lib/format";
import { autosLabel } from "@/components/AutosTag";

// Lote 165 — regras puras da tela Processos (antes dentro de pages/Processos.jsx):
// datas, etiquetas, "próxima ação", revisão rápida e o motor de filtros. Sem React aqui, para testar e reaproveitar.

export const PRAZO_SOON_DAYS = 7;
export const HEARING_TAB_DAYS = 30;
export const PARADOS_DEFAULT = 180;
export const PARADOS_OPTIONS = [
  { days: 30, label: "30 dias" },
  { days: 90, label: "3 meses" },
  { days: 180, label: "6 meses" },
  { days: 365, label: "1 ano" },
];
export const paradosLabel = (days) => (PARADOS_OPTIONS.find((o) => o.days === days)?.label || `${days} dias`);
export const PARADOS_KEY = "iapauta_parados_dias";
export const POR_PAGINA = 25;

export const brDate = (iso) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || "");
  return m ? `${m[3]}/${m[2]}` : "";
};
export const brDateFull = (iso) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || "");
  return m ? `${m[3]}/${m[2]}/${m[1]}` : "";
};
// "14:32" hoje, "ontem às 14:32", "15/10 às 14:32" (horário de Brasília)
export function syncMoment(isoValue) {
  if (!isoValue) return null;
  const d = new Date(isoValue);
  if (Number.isNaN(d.getTime())) return null;
  const tz = { timeZone: "America/Sao_Paulo" };
  const day = (x) => x.toLocaleDateString("en-CA", tz);
  const time = d.toLocaleTimeString("pt-BR", { ...tz, hour: "2-digit", minute: "2-digit" });
  const y = new Date(); y.setDate(y.getDate() - 1);
  if (day(d) === day(new Date())) return `hoje às ${time}`;
  if (day(d) === day(y)) return `ontem às ${time}`;
  return `${d.toLocaleDateString("pt-BR", { ...tz, day: "2-digit", month: "2-digit" })} às ${time}`;
}
export function relTime(iso) {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  const min = Math.max(0, Math.round((Date.now() - t) / 60000));
  if (min < 2) return "agora";
  if (min < 60) return `há ${min} min`;
  if (min < 60 * 24) return `há ${Math.round(min / 60)}h`;
  return `há ${Math.round(min / 1440)} d`;
}

export function partiesRaw(p) {
  const author = (p.party_author || "").trim();
  const defendant = (p.party_defendant || "").trim();
  const raw = author && defendant ? `${author} x ${defendant}` : author || defendant;
  return raw ? formatParties(raw) : "";
}

// "trt15" → "TRT-15", "tjsp" → "TJSP", "trf3" → "TRF-3"
export function tribunalLabel(alias) {
  const a = (alias || "").trim().toLowerCase();
  if (!a) return "";
  const m = /^(trt|trf|tre|trm)(\d+)$/.exec(a);
  return m ? `${m[1].toUpperCase()}-${parseInt(m[2], 10)}` : a.toUpperCase();
}

export const localOf = (p) => (p.comarca || p.vara || "").trim();

// Dias desde a última movimentação capturada (ou, sem nenhuma, desde o cadastro)
export function diasParado(p) {
  const ref = (p.last_movement_date || p.last_movement_at || p.created_at || "").slice(0, 10);
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ref);
  if (!m) return 0;
  const t = Date.UTC(+m[1], +m[2] - 1, +m[3]);
  return Math.floor((Date.now() - t) / 86400000);
}
export const isParado = (p, limite) => !p.arquivado && diasParado(p) > limite;
export function tempoParado(p) {
  const d = diasParado(p);
  return d >= 60 ? `${Math.round(d / 30)} meses` : `${d} dias`;
}

// Situação do cadastro no app (≠ situação judicial): ativo | arquivado | extinto
export const situacaoCadastro = (p) => (!p.arquivado ? "ativo" : p.situacao_arquivo === "extinto" ? "extinto" : "arquivado");

// Etiqueta suave da situação (fundo claro, texto discreto)
export function etiqueta(p, limite) {
  if (p.arquivado) return p.situacao_arquivo === "extinto" ? { tom: "extinto", texto: "Extinto" } : { tom: "arquivado", texto: "Arquivado" };
  if (p.prazo_vencido) return { tom: "vencido", texto: "Prazo vencido" };
  if (p.status_kind === "pendencia") return { tom: "atencao", texto: "Para revisar" };
  if (p.revisao_vencida) return { tom: "atencao", texto: "Revisão vencida" };
  if (p.status_kind === "audiencia") {
    const d = p.next_hearing?.days;
    return { tom: "audiencia", texto: d === 0 ? "Audiência hoje" : d === 1 ? "Audiência amanhã" : `Audiência em ${d} dias` };
  }
  if (isParado(p, limite)) return { tom: "parado", texto: `Sem movimentação capturada há ${tempoParado(p)}` };
  return null;
}
export const TOM_CLS = {
  vencido: "bg-red-50 text-red-700 border-red-200",
  atencao: "bg-blue-50 text-blue-700 border-blue-200",
  audiencia: "bg-amber-50 text-amber-800 border-amber-200",
  parado: "bg-stone-100 text-stone-600 border-stone-200",
  arquivado: "bg-stone-100 text-stone-600 border-stone-200",
  extinto: "bg-stone-100 text-stone-500 border-stone-200",
};

// Lote 166 — providências do processo (só dados reais). O backend (GET /processos, /escritorio/processos) devolve `acoes`
// já na ordem em que vencem: prazo vencido sem baixa → pendência de conferência → audiência/prazo por data → revisão
// vencida. Cada uma: { kind, label (o que fazer), motivo (por quê), to, grupo, data }. A primeira é a "próxima ação".
// Sem `acoes` na resposta (servidor antigo) monta-se a mesma lista com os campos de antes.
const GRUPO = { prazo_vencido: 0, revisar_itens: 1, revisar_prazo: 1, conferir_audiencia: 1, ata: 1, ver_audiencia: 2, ver_prazo: 2, revisao_vencida: 3 };
export function acoesDoProcesso(p) {
  if (p.arquivado) return [];
  if (Array.isArray(p.acoes)) return p.acoes;
  const out = [];
  if (p.pendencias > 0) {
    out.push({
      kind: p.next_action?.kind || "revisar_itens", label: p.next_action?.label || (p.pendencias === 1 ? "Revisar 1 item" : `Revisar ${p.pendencias} itens`),
      motivo: p.pendencias === 1 ? "1 item aguardando conferência" : `${p.pendencias} itens aguardando conferência`, to: p.next_action?.to, grupo: 1, data: null,
    });
  }
  if (p.next_hearing) {
    const h = p.next_hearing;
    out.push({ kind: "ver_audiencia", label: "Ver audiência", to: p.next_hearing.pub_id ? `/audiencia/${h.pub_id}` : undefined, grupo: 2, data: h.date,
      motivo: `Audiência ${brDate(h.date)}${h.time ? ` às ${h.time}` : ""} · ${h.days === 0 ? "hoje" : h.days === 1 ? "amanhã" : `em ${h.days} dias`}` });
  }
  if (p.next_prazo && p.next_prazo.days != null && p.next_prazo.days <= PRAZO_SOON_DAYS) {
    const z = p.next_prazo;
    out.push({ kind: "ver_prazo", label: "Ver prazo", to: z.pub_id ? `/audiencia/${z.pub_id}` : undefined, grupo: 2, data: z.date,
      motivo: `Prazo ${brDate(z.date)} · ${z.days === 0 ? "vence hoje" : z.days === 1 ? "vence amanhã" : `vence em ${z.days} dias`}` });
  }
  return out;
}
// "Atenção" = pendência concreta (conferir/baixar, prazo vencido sem baixa, revisão vencida). Audiência futura confirmada não é.
export const emAtencao = (p) => (typeof p.atencao === "boolean" ? p.atencao : p.status_kind === "pendencia");

// Botão da próxima providência: a do backend; senão, "Atualizar agora" (parado) ou "Vincular cliente".
export function nextCta(p, limite) {
  if (p.arquivado) return { kind: "reativar", label: "Reativar" };
  if (p.next_action) return { kind: "link", label: p.next_action.label, to: p.next_action.to, primary: p.next_action.kind !== "ver_prazo" && p.next_action.kind !== "ver_audiencia" };
  if (isParado(p, limite)) return { kind: "atualizar", label: "Atualizar agora" };
  if (!p.client_id) return { kind: "vincular", label: "Vincular cliente" };
  return null;
}

// Lote 100 — ação rápida "Já revisado" (ver histórico no Lote 100). Mantida igual.
export function quickReview(p) {
  const itens = p.pend_itens || [];
  if (p.arquivado || itens.length === 0) return null;
  const revisaveis = itens.filter((i) => i.kind === "pub" && i.pode_revisar);
  if (itens.length === 1) {
    const it = itens[0];
    if (it.kind === "ata") return it.pendencia_id ? { mode: "ata", label: "Ata sem prazos", item: it } : null;
    return it.pode_revisar ? { mode: "um", label: "Já revisado", item: it } : null;
  }
  return revisaveis.length > 0 ? { mode: "todos", label: "Já revisei todos", itens, revisaveis } : null;
}
export function itemLabel(i) {
  const d = brDate(i.date);
  if (i.kind === "ata") return `Ata da audiência${d ? ` de ${d}` : ""} — fica pendente (use “Baixar ata”)`;
  if (i.event_type === "prazo") return `${i.titulo}${d ? ` — vence ${d}` : " — sem data"}`;
  return `${i.titulo}${d ? ` ${d}` : " — sem data"}${i.time ? ` às ${i.time}` : ""}`;
}

// ---------------- filtros ----------------
export const SIT_OPCOES = [
  { key: "audiencia", label: "Com audiência nos próximos 30 dias" },
  { key: "prazo", label: "Com prazo nos próximos 7 dias" },
  { key: "sem_cliente", label: "Sem cliente vinculado" },
  { key: "sem_resp", label: "Sem responsável", soEscritorio: true },
  { key: "parados", label: "Sem movimentação capturada" },
];
export const ARQ_OPCOES = [
  { key: "ativos", label: "Ativos" },
  { key: "pausados", label: "Arquivados e extintos" },
  { key: "arquivados", label: "Arquivados no app" },
  { key: "extintos", label: "Extintos" },
  { key: "todos", label: "Todos" },
];
export const ARQ_ROTULO = Object.fromEntries(ARQ_OPCOES.map((o) => [o.key, o.label]));
export const FILTROS_PADRAO = { cliente: "all", area: "all", tribunal: "all", local: "all", sit: [], arq: "ativos" };

// Quantos filtros do painel estão ligados (a busca e a aba não contam). `responsavel` vem do escopo.
export function contarFiltros(f, responsavel = "") {
  return (f.cliente !== "all" ? 1 : 0) + (f.area !== "all" ? 1 : 0) + (f.tribunal !== "all" ? 1 : 0) + (f.local !== "all" ? 1 : 0)
    + f.sit.length + (f.arq !== "ativos" ? 1 : 0) + (responsavel ? 1 : 0);
}

// Busca (CNJ com ou sem pontuação, partes, cliente, vara, comarca, apelido, tribunal) + filtros do painel.
export function filtrarProcessos(items, { f, busca, nomeCliente, parados }) {
  const q = (busca || "").trim().toLowerCase();
  const digitsQ = q.replace(/\D/g, "");
  const vistos = new Set();
  return (items || []).filter((p) => {
    if (vistos.has(p.processo_id)) return false;   // contadores de processos ÚNICOS
    vistos.add(p.processo_id);
    const sc = situacaoCadastro(p);
    if (f.arq === "ativos" && sc !== "ativo") return false;
    if (f.arq === "pausados" && sc === "ativo") return false;
    if (f.arq === "arquivados" && sc !== "arquivado") return false;
    if (f.arq === "extintos" && sc !== "extinto") return false;
    if (f.cliente !== "all" && p.client_id !== f.cliente) return false;
    if (f.area !== "all" && p.case_area !== f.area) return false;
    if (f.tribunal !== "all" && p.tribunal_alias !== f.tribunal) return false;
    if (f.local !== "all" && localOf(p) !== f.local) return false;
    if (f.sit.includes("audiencia") && !(p.next_hearing && p.next_hearing.days != null && p.next_hearing.days <= HEARING_TAB_DAYS)) return false;
    if (f.sit.includes("prazo") && !(p.next_prazo && p.next_prazo.days != null && p.next_prazo.days <= PRAZO_SOON_DAYS)) return false;
    if (f.sit.includes("sem_cliente") && p.client_id) return false;
    if (f.sit.includes("sem_resp") && p.responsavel_id) return false;
    if (f.sit.includes("parados") && !(diasParado(p) > parados)) return false;
    if (!q) return true;
    const blob = [
      p.numero, p.apelido, p.party_author, p.party_defendant, p.vara, p.comarca,
      nomeCliente(p.client_id), p.tribunal_alias, p.autos && p.autos !== "principal" ? autosLabel(p.autos, p.autos_nome) : "",
    ].join(" ").toLowerCase();
    if (blob.includes(q)) return true;
    return digitsQ.length >= 3 && (p.numero || "").replace(/\D/g, "").includes(digitsQ);
  });
}

export const ABAS = {
  todos: () => true,
  atencao: (p) => emAtencao(p),
  prioritarios: (p) => !!p.prioritario,
};

// Lote 166 — ordenação (a estrela NÃO muda a ordem; quem quer só os prioritários usa a aba).
export const ORDENS = [
  { key: "acao", label: "Próxima ação" },
  { key: "movimentacao", label: "Última movimentação" },
  { key: "cliente", label: "Cliente (A–Z)" },
  { key: "cadastro", label: "Cadastro mais recente" },
];
export const ORDEM_PADRAO = "acao";
const FIM = "9999-99-99";
export function ordenarProcessos(lista, ordem = ORDEM_PADRAO, nomeCliente = () => "") {
  const mov = (p) => p.last_movement_at || p.created_at || "";
  const recente = (a, b) => mov(b).localeCompare(mov(a));
  const titulo = (p) => (nomeCliente(p.client_id) || (p.apelido || "").trim() || p.numero || "").toLowerCase();
  const porAcao = (a, b) => {
    const x = acoesDoProcesso(a)[0]; const y = acoesDoProcesso(b)[0];
    const gx = x ? (x.grupo ?? GRUPO[x.kind] ?? 2) : 4; const gy = y ? (y.grupo ?? GRUPO[y.kind] ?? 2) : 4;
    if (gx !== gy) return gx - gy;                                   // vencidas primeiro, depois pendências, depois o que vem aí
    return (x?.data || FIM).localeCompare(y?.data || FIM) || recente(a, b);
  };
  const cmp = {
    acao: porAcao,
    movimentacao: (a, b) => (!!b.last_movement_at - !!a.last_movement_at) || recente(a, b),
    cliente: (a, b) => (!a.client_id - !b.client_id) || titulo(a).localeCompare(titulo(b), "pt-BR"),
    cadastro: (a, b) => (b.created_at || "").localeCompare(a.created_at || ""),
  }[ordem] || porAcao;
  return [...lista].sort((a, b) => cmp(a, b) || (a.numero || "").localeCompare(b.numero || ""));
}

export { toTitleCasePt };
